import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFunnelV3Store } from '../../../store/funnelV3Store';
import { trackQuizStart, trackQuizStep } from '../analytics/funnelV3Analytics';
import AffirmToast from '../AffirmToast';
import FunnelV3Screen from '../FunnelV3Screen';
import ReferralInviteBanner from '../ReferralInviteBanner';

const OPTION_VALUES = ['community', 'gift_cards', 'guidance', 'all'] as const;
const OPTION_EMOJIS: Record<string, string> = {
    community:  '🤝',
    gift_cards: '🎁',
    guidance:   '🧭',
    all:        '✨',
};

const MotivationStep: React.FC = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore — react-i18next deep type issue with TS 4.x
    const { t } = useTranslation();
    const [selected, setSelected] = useState<string | null>(null);
    const [toastMsg, setToastMsg] = useState('');
    const [toastVisible, setToastVisible] = useState(false);
    const { updateField, setStep, data } = useFunnelV3Store();

    const options = OPTION_VALUES.map((value) => ({
        value,
        emoji: OPTION_EMOJIS[value],
        label: t(`motivation.options.${value}`),
    }));

    const handleSelect = (value: string) => {
        if (selected) return;
        setSelected(value);
        setToastMsg(t(`motivation.toasts.${value}`) as string);
        setToastVisible(true);
        // The next step is split out of the landing bundle. Fetch it while the
        // affirmation is visible so advancing remains instant.
        void import('./MotivationInterstitial');
        setTimeout(() => {
            updateField('motivation', value);
            trackQuizStart({ motivation: value });
            trackQuizStep('motivation', 1, value);
            setStep('motivation_intro');
        }, 1700);
    };

    return (
        <FunnelV3Screen hideCta contentClassName="pt-10 font-sans">
            {data.referrerFirstName ? (
                <ReferralInviteBanner
                    message={t('emailCapture.referralBanner', {
                        referrerFirstName: data.referrerFirstName,
                    }) as string}
                />
            ) : null}
            <span className="inline-block bg-navy text-gold text-[10px] font-bold uppercase tracking-[2px] px-3 py-1 rounded-full mb-4">
                {t('badge.walkMoreEarnRewards')}
            </span>
            <h1 className="text-[28px] font-bold text-[#0F1F3D] leading-[1.3] tracking-normal mb-3">
                {t('motivation.title')}
            </h1>
            <div className="flex flex-col gap-3">
                {options.map((opt) => {
                    const isSelected = selected === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSelect(opt.value)}
                            className={`
                                w-full min-h-[58px] flex items-center gap-4 px-5 py-5 rounded-2xl border-2
                                bg-white shadow-sm text-left transition-all duration-150 cursor-pointer active:scale-[0.99]
                                ${isSelected
                                    ? 'border-[#369fff] bg-[#E8F4FF] scale-[1.02] shadow-[0_4px_14px_rgba(54,159,255,0.3)]'
                                    : 'border-transparent'
                                }
                            `}
                        >
                            <span className="text-[32px] flex-shrink-0">{opt.emoji}</span>
                            <span className="flex-1 font-bold text-[17px] text-[#0F1F3D]">
                                {opt.label}
                            </span>
                            <div className={`
                                w-[24px] h-[24px] rounded-full border-2 flex items-center justify-center flex-shrink-0
                                ${isSelected ? 'bg-[#369fff] border-[#369fff]' : 'border-[#C8D8E8] bg-white'}
                            `}>
                                {isSelected && <span className="text-white text-[11px] font-black">&#10003;</span>}
                            </div>
                        </button>
                    );
                })}
            </div>
            <AffirmToast message={toastMsg} visible={toastVisible} />
        </FunnelV3Screen>
    );
};

export default MotivationStep;
